/** Human-readable AI-answer copy helpers. No Vue/store imports. */

import {fixCzechGluedPrepositions, splitGluedHeadingBody} from './articleSanitize';

export function decodePercentChunks(value: string): string {
    return String(value || '').replace(/(?:%[0-9A-Fa-f]{2})+/g, (chunk) => {
        try {
            return decodeURIComponent(chunk);
        } catch {
            return chunk;
        }
    });
}

export function humanizeAiAnswerMarkdown(raw: string): string {
    let text = decodePercentChunks(String(raw || ''));
    if (!text.trim()) {
        return '';
    }
    text = text.replace(/\[\[(\d+)\]\]\((https?:\/\/[^)\s]+)\)/gi, '');
    text = text.replace(/\[(\d+)\]\((https?:\/\/[^)\s]+)\)/gi, '');
    text = text.replace(/\[\[(\d+)\]\]/g, '');
    text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gi, '$1');
    text = text.replace(/\((https?:\/\/[^)\s]+)\)/gi, '');
    text = text.replace(/https?:\/\/[^\s)>\]]+/gi, '');
    text = text.replace(/^\s{0,3}#{1,6}\s+/gm, '');
    text = text.replace(/\s+#{1,6}\s+/g, ' ');
    text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
    text = text.replace(/__([^_]+)__/g, '$1');
    text = text.replace(/(^|[^\w])\*([^*\n]+)\*(?!\*)/g, '$1$2');
    text = text.replace(/\[(\d+)\]/g, '');
    text = text.replace(/[ \t]+\n/g, '\n');
    text = text.replace(/\n{3,}/g, '\n\n');
    text = text.replace(/[^\S\n]{2,}/g, ' ');
    text = text.replace(/\s+([,.;:!?])/g, '$1');
    text = text.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');
    text = splitGluedHeadingBody(fixCzechGluedPrepositions(text));
    return text.replace(/[ \t]+/g, ' ').replace(/\n /g, '\n').trim();
}

export const LIVE_ANSWER_PREVIEW_CHARS = 280;
export const LIVE_ANSWER_PREVIEW_LINES = 5;

export function liveAnswerBody(answer?: {markdown?: string; citedSnippets?: string[]} | null): string {
    if (!answer) {
        return '';
    }
    const fromMarkdown = humanizeAiAnswerMarkdown(answer.markdown || '');
    if (fromMarkdown) {
        return fromMarkdown;
    }
    return (answer.citedSnippets || [])
        .map((item) => humanizeAiAnswerMarkdown(item))
        .filter(Boolean)
        .join('\n\n');
}

export function liveAnswerNeedsExpand(
    text: string,
    maxChars = LIVE_ANSWER_PREVIEW_CHARS,
    maxLines = LIVE_ANSWER_PREVIEW_LINES
): boolean {
    const value = String(text || '').trim();
    if (!value) {
        return false;
    }
    return value.split(/\n/).length > maxLines || value.length > maxChars;
}

export function normalizeBriefPhrase(value: string | null | undefined): string {
    return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&[a-z]+;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

export function textContainsPhrase(haystack: string, phrase: string): boolean {
    const text = normalizeBriefPhrase(haystack);
    const needle = normalizeBriefPhrase(phrase);
    return !!text && !!needle && text.includes(needle);
}

export function joinNaturalList(items: string[], locale: string): string {
    const clean = items.map((item) => String(item || '').replace(/\s+/g, ' ').trim()).filter(Boolean);
    if (!clean.length) {
        return '';
    }
    if (clean.length === 1) {
        return clean[0];
    }
    const conjunction = locale === 'cs' ? ' a ' : ' and ';
    if (clean.length === 2) {
        return clean[0] + conjunction + clean[1];
    }
    return clean.slice(0, -1).join(', ') + conjunction + clean[clean.length - 1];
}

function ensureSentence(text: string): string {
    const trimmed = String(text || '').replace(/\s+/g, ' ').trim().replace(/[.?!]+$/g, '');
    if (!trimmed) {
        return '';
    }
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1) + '.';
}

function injectBeforeLastClose(html: string, addition: string): string {
    const current = String(html || '');
    if (!current.trim()) {
        return `<p>${addition.trim()}</p>`;
    }
    if (/<\/p>\s*$/i.test(current)) {
        return current.replace(/<\/p>\s*$/i, addition + '</p>');
    }
    const lastP = current.lastIndexOf('</p>');
    if (lastP !== -1) {
        return current.slice(0, lastP) + addition + current.slice(lastP);
    }
    return current.replace(/\s*$/, '') + addition;
}

export function insertPhrasesIntoHtml(
    html: string,
    phrases: string[],
    kind: 'lsi' | 'fact',
    locale: string
): {html: string; inserted: string[]} {
    const missing = phrases
        .map((item) => String(item || '').replace(/\s+/g, ' ').trim())
        .filter((item) => item && !textContainsPhrase(html, item));
    if (!missing.length) {
        return {html, inserted: []};
    }
    let addition = '';
    if (kind === 'lsi') {
        const list = joinNaturalList(missing, locale);
        addition = locale === 'cs'
            ? ` V textu se objevují také ${list}.`
            : ` Related terms to name in the copy include ${list}.`;
    } else {
        addition = ' ' + missing.map(ensureSentence).filter(Boolean).join(' ');
    }
    return {html: injectBeforeLastClose(html, addition), inserted: missing};
}
